import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { checkLoginStatus, logoutUser } from "../services/authRequests";
import { clearCartOnLogin } from "../services/nonAuthRequets";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  // Initialize auth state from sessionStorage only.
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem("isAuthenticated") === "true";
  });
  const [user, setUser] = useState(() => {
    const storedUser = sessionStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [isChecking, setIsChecking] = useState(true);

  // Helper: Save auth state to sessionStorage
  const saveAuthState = (auth, userData) => {
    sessionStorage.setItem("isAuthenticated", auth ? "true" : "false");
    sessionStorage.setItem("user", JSON.stringify(userData));
  };

  // Helper: Clear stored auth state
  const clearAuthState = () => {
    sessionStorage.removeItem("isAuthenticated");
    sessionStorage.removeItem("user");
  };

  // Check login status on mount (uses cookies on the backend to validate tokens)
  const checkUserLogin = async () => {
    //console.log("Checking login status...");
    try {
      const res = await checkLoginStatus();
      if (res) {
        setIsAuthenticated(true);
        setUser(res);
        saveAuthState(true, res);
        if (localStorage.getItem("clearCartAfterLogin") === "true") {
          clearCartOnLogin();
          localStorage.removeItem("clearCartAfterLogin"); 
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        clearAuthState();
      }
    } catch (error) {
      console.error("Error checking login status:", error);
      setIsAuthenticated(false);
      setUser(null);
      clearAuthState();
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkUserLogin();
  }, []);

  // Login function without "Remember Me"
  const login = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
    saveAuthState(true, userData);
  };

  // Logout function uses useNavigate for SPA-friendly redirection.
  const logout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error("Error during logout:", error);
    }
    setIsAuthenticated(false);
    setUser(null);
    clearAuthState();
    navigate("/login", { replace: true });
  };

  // Handle forced logout (e.g., from other tabs)
  useEffect(() => {
    const handleForceLogout = () => {
      logout();
    };
    window.addEventListener("forceLogout", handleForceLogout);
    return () => {
      window.removeEventListener("forceLogout", handleForceLogout);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, isChecking, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};


